import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { Webhook, Plus, Trash2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const WebhookManager = ({ reducedMotion }) => {
  const [webhooks, setWebhooks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newWebhook, setNewWebhook] = useState({ url: '', event: 'call.completed' });

  useEffect(() => {
    fetchWebhooks();
  }, []);

  // TODO Phase Audit Fix B: wire to real /api/webhooks/v2/list endpoint when backend implements it.
  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      // No real endpoint yet — start with an empty list so the EmptyState renders.
      setWebhooks([]);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
      setWebhooks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterWebhook = async () => {
    if (!newWebhook.url) {
      toast.error('Please enter a webhook URL');
      return;
    }

    try {
      // Mock registration - in production would POST to /api/webhooks/v2/register
      const webhook = {
        id: `wh_${Date.now()}`,
        ...newWebhook,
        status: 'active',
        last_triggered: 'Never',
        success_rate: 1.0
      };
      setWebhooks([...(webhooks || []), webhook]);
      setNewWebhook({ url: '', event: 'call.completed' });
      toast.success('Webhook registered successfully');
    } catch (error) {
      toast.error('Failed to register webhook');
    }
  };

  const handleDeleteWebhook = async (id) => {
    setWebhooks((webhooks || []).filter(w => w.id !== id));
    toast.success('Webhook deleted');
  };

  const handleTestWebhook = async (id) => {
    toast.info('Sending test payload...');
    setTimeout(() => {
      toast.success('Test webhook delivered successfully');
    }, 1500);
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="webhook-manager-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Webhook className="w-5 h-5 text-indigo-400" />
          Webhook Manager
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Register and manage outbound webhooks with retry logic
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* New Webhook Form */}
        <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 mb-6">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Register New Webhook</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="url"
              placeholder="https://api.example.com/webhook"
              value={newWebhook.url}
              onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500"
              data-testid="webhook-url-input"
            />
            <select
              value={newWebhook.event}
              onChange={(e) => setNewWebhook({ ...newWebhook, event: e.target.value })}
              className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
              data-testid="webhook-event-select"
            >
              <option value="call.completed">Call Completed</option>
              <option value="call.failed">Call Failed</option>
              <option value="appointment.booked">Appointment Booked</option>
              <option value="lead.updated">Lead Updated</option>
            </select>
          </div>
          <Button
            onClick={handleRegisterWebhook}
            className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="register-webhook-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Register Webhook
          </Button>
        </div>

        {/* Webhooks List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : webhooks && webhooks.length > 0 ? (
          <div className="space-y-3">
            {webhooks.map((webhook, index) => (
              <div
                key={webhook.id}
                className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
                style={{
                  animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                }}
                data-testid={`webhook-item-${webhook.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid={`webhook-status-${webhook.id}`}>
                        {webhook.status}
                      </Badge>
                      <p className="text-xs text-slate-500">{webhook.event}</p>
                    </div>
                    <p className="text-sm text-slate-300 font-mono" data-testid="webhook-url">{webhook.url}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestWebhook(webhook.id)}
                      className="text-slate-400 hover:text-slate-100"
                      data-testid={`test-webhook-${webhook.id}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-webhook-${webhook.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-700/30">
                  <div>
                    <p className="text-xs text-slate-400">Last Triggered</p>
                    <p className="text-sm text-slate-300 mt-1" data-testid="last-triggered">{webhook.last_triggered}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Success Rate</p>
                    <div className="flex items-center gap-2 mt-1">
                      {webhook.success_rate >= 0.95 ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <XCircle className="w-4 h-4 text-amber-400" />
                      )}
                      <p className="text-sm text-slate-300" data-testid="success-rate">{(webhook.success_rate * 100).toFixed(0)}%</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Webhook}
            title="No data yet"
            description="Data will appear once it flows through."
          />
        )}
      </CardContent>
    </Card>
  );
};

export default WebhookManager;